import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ProductService, ProductInstanceService } from '../services/product.service';
import { queryKeys } from './query-keys';
import type { Product } from '../entities/product.entity';

export function useProducts(filters?: Parameters<typeof ProductService.getMany>[0]) {
  return useQuery({
    queryKey: queryKeys.products.filtered(filters),
    queryFn: () => ProductService.getMany(filters),
  });
}

export function useProduct(id: string) {
  return useQuery({
    queryKey: queryKeys.products.detail(id),
    queryFn: () => ProductService.getById(id),
    enabled: !!id,
  });
}

export function useProductsByIds(ids: string[]) {
  return useQuery({
    queryKey: queryKeys.products.byIds(ids),
    queryFn: () => ProductService.getByIds(ids),
    enabled: ids.length > 0,
  });
}

export function useCreateProduct() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (product: Omit<Product, 'id' | 'createdAt' | 'updatedAt'>) =>
      ProductService.create(product),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.products.all() });
    },
  });
}

export function useUpdateProduct() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: Partial<Product> }) =>
      ProductService.update(id, updates),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.products.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.products.all() });
    },
  });
}

export function useDeleteProduct() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => ProductService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.products.all() });
    },
  });
}

export function useProductExternalMappings(productId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.products.externalMappings(productId ?? ''),
    queryFn: () => ProductService.getExternalMappings(productId as string),
    enabled: !!productId,
  });
}
